import { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

interface SpendModalProps {
  onClose: () => void;
  onSuccess: () => void;
  currentBalance: number;
}

export default function SpendModal({ onClose, onSuccess, currentBalance }: SpendModalProps) {
  const { user } = useAuth();
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('product');
  const [loading, setLoading] = useState(false);

  const categories = [
    { value: 'product', label: '상품 구매', icon: 'ri-shopping-bag-line' },
    { value: 'donation', label: '기부·헌금', icon: 'ri-heart-line' },
    { value: 'event', label: '행사 참가비', icon: 'ri-ticket-line' },
    { value: 'content', label: '콘텐츠 구매', icon: 'ri-film-line' },
    { value: 'gift', label: '선물하기', icon: 'ri-gift-line' },
    { value: 'service', label: '서비스 이용', icon: 'ri-service-line' },
    { value: 'membership', label: '멤버십', icon: 'ri-vip-crown-line' },
    { value: 'other', label: '기타', icon: 'ri-more-line' },
  ];

  const quickAmounts = [1000, 3000, 5000, 10000, 20000, 50000];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !amount || !description) {
      alert('모든 항목을 입력해주세요.');
      return;
    }

    const spendAmount = parseInt(amount);
    if (spendAmount <= 0) {
      alert('사용 금액은 0보다 커야 합니다.');
      return;
    }

    if (spendAmount > currentBalance) {
      alert('보유 달란트가 부족합니다.');
      return;
    }

    setLoading(true);

    try {
      // 달란트 포인트 업데이트
      const { data: currentPoints, error: fetchError } = await supabase
        .from('talent_points')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (fetchError) throw fetchError;

      const newBalance = (currentPoints?.balance || 0) - spendAmount;
      const newTotalSpent = (currentPoints?.total_spent || 0) + spendAmount;

      const { error: updateError } = await supabase
        .from('talent_points')
        .update({
          balance: newBalance,
          total_spent: newTotalSpent,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      // 거래 내역 추가
      const { error: transactionError } = await supabase
        .from('point_transactions')
        .insert({
          user_id: user.id,
          amount: -spendAmount,
          type: 'spend',
          description: `${categories.find(c => c.value === category)?.label}: ${description}`,
          created_at: new Date().toISOString(),
        });

      if (transactionError) throw transactionError;

      alert(`${spendAmount.toLocaleString()} 달란트가 사용되었습니다!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('달란트 사용 실패:', error);
      alert('달란트 사용에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-red-500 to-rose-500 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <i className="ri-subtract-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold">달란트 사용</h2>
                <p className="text-red-100 text-sm">보유 달란트: {currentBalance.toLocaleString()}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              사용 카테고리
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    category === cat.value
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-red-300'
                  }`}
                >
                  <i className={`${cat.icon} text-2xl mb-2 ${
                    category === cat.value ? 'text-red-600' : 'text-gray-400'
                  }`}></i>
                  <p className={`text-sm font-semibold ${
                    category === cat.value ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {cat.label}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              사용 금액
            </label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
              {quickAmounts.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => setAmount(amt.toString())}
                  disabled={amt > currentBalance}
                  className={`py-3 px-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    amt > currentBalance
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 hover:bg-red-50 hover:text-red-600 hover:border-red-500 border-2 border-transparent'
                  }`}
                >
                  {amt.toLocaleString()}
                </button>
              ))}
            </div>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="직접 입력"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none text-lg"
                min="1"
                max={currentBalance}
                required
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                달란트
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              * 보유 달란트: {currentBalance.toLocaleString()} 달란트
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              사용 내용
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="어디에 달란트를 사용하셨나요?"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none resize-none"
              rows={4}
              maxLength={200}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {description.length}/200자
            </p>
          </div>

          {amount && parseInt(amount) > 0 && (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">사용 금액</span>
                <span className="text-lg font-bold text-red-600">
                  -{parseInt(amount).toLocaleString()} 달란트
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <span className="text-gray-600">사용 후 잔액</span>
                <span className="text-lg font-bold text-gray-900">
                  {(currentBalance - parseInt(amount)).toLocaleString()} 달란트
                </span>
              </div>
            </div>
          )}

          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-xl text-red-600 mt-0.5"></i>
              <div className="text-sm text-red-800">
                <p className="font-semibold mb-1">사용 안내</p>
                <ul className="space-y-1 text-red-700">
                  <li>• 사용된 달란트는 취소할 수 없습니다</li>
                  <li>• 보유 달란트 범위 내에서만 사용 가능합니다</li>
                  <li>• 사용 내역은 거래 내역에서 확인할 수 있습니다</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-4 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading || !amount || parseInt(amount) > currentBalance}
              className="flex-1 py-4 bg-gradient-to-r from-red-500 to-rose-500 text-white font-semibold rounded-xl hover:from-red-600 hover:to-rose-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              {loading ? '처리 중...' : '사용하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
